import { useQuery } from '@tanstack/react-query';
import { fetchMyPermissionsApi, fetchRoleCatalogueApi } from '../apis/user';
import { useAuth } from '../context/auth';
import { MyPermissions, Permission } from '../types';

/**
 * What the signed-in user may do, straight from the server.
 *
 * The admin panel used to infer this from the role name — `role === 'admin'`,
 * scattered across the route guard, the sidebar and individual pages. With one
 * staff role that was merely duplicated logic; with eight it is a guarantee that
 * the UI and the API will disagree, and the visible symptom of that is a page
 * that renders fully and then 403s on every request it makes.
 *
 * So the client asks rather than deduces. The one thing it must not do is treat
 * this as security: the backend `requirePermission` middleware is the boundary,
 * and this only decides what is worth showing.
 */
export const useMyPermissions = () => {
  const { currentUser } = useAuth();

  return useQuery<MyPermissions>({
    queryKey: ['permissions', currentUser?._id],
    queryFn: fetchMyPermissionsApi,
    enabled: Boolean(currentUser),
    // Roles change rarely, and a stale grant costs nothing — the API still
    // refuses the action. A stale *revocation* is the case worth bounding, so
    // this is minutes rather than the session.
    staleTime: 1000 * 60 * 5,
  });
};

/**
 * `can('course:write')` — the single question every gate in the admin panel
 * should ask.
 *
 * Returns false while loading, which means a freshly opened page shows nothing
 * for a moment rather than flashing controls that may then vanish. `isLoading`
 * is exposed so callers that would rather show a skeleton can.
 */
export const useCan = () => {
  const { data, isLoading } = useMyPermissions();
  const held = new Set(data?.permissions ?? []);

  return {
    can: (permission: Permission) => held.has(permission),
    canAny: (...permissions: Permission[]) => permissions.some((p) => held.has(p)),
    roles: data?.roles ?? [],
    primaryRole: data?.primaryRole,
    isLoading,
  };
};

/**
 * The role catalogue, served by the same table the middleware enforces so the
 * picker cannot offer a role the API will reject.
 */
export const useRoleCatalogue = (options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: ['role-catalogue'],
    queryFn: fetchRoleCatalogueApi,
    // The endpoint itself requires USER_ROLE_WRITE, so callers who cannot assign
    // roles should pass `enabled: false` rather than fetch a certain 403.
    enabled: options?.enabled ?? true,
    staleTime: 1000 * 60 * 30,
  });
